import { type ReactNode, useCallback, useState } from "react";
import type { GrantRow } from "../../shared/rbac/crossResourceAvailability";
import type { GrantEntry } from "../../shared/rbac/roles";
import { ConfirmModal } from "../components/ConfirmModal";
import {
  Count,
  type DisconnectImpact,
  DisconnectWarning,
} from "../components/DisconnectWarning";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "./useConnectorDisconnectImpact";

// Guards a connector grant commit behind a confirmation when the change would
// disconnect the connector from workflows or git sync in spaces losing access.
// Commits with no visible impact proceed silently.
export function useConnectorDisconnectGuard({
  appId,
  appName,
  scopeGrants,
}: {
  appId: string;
  appName: string;
  scopeGrants: readonly GrantRow[];
}): {
  guardCommit: (
    grants: GrantEntry[],
    proceed: (grants: GrantEntry[]) => void
  ) => void;
  modal: ReactNode;
} {
  const computeImpact = useConnectorDisconnectImpact(appId);
  const [pending, setPending] = useState<{
    grants: GrantEntry[];
    proceed: (grants: GrantEntry[]) => void;
    impact: DisconnectImpact;
  } | null>(null);

  const guardCommit = useCallback(
    (grants: GrantEntry[], proceed: (grants: GrantEntry[]) => void) => {
      const impact = computeImpact(scopeGrants, grants);
      if (impact.workflows.length === 0 && impact.gitSpaceNames.length === 0) {
        proceed(grants);
        return;
      }
      setPending({ grants, proceed, impact });
    },
    [computeImpact, scopeGrants]
  );

  const workflowCount = pending?.impact.workflows.length ?? 0;
  const hasWorkflows = workflowCount > 0;
  const chatCount = useAffectedChatCount(
    appId,
    pending?.impact.affectedUserIds ?? []
  );
  const modal = (
    <ConfirmModal
      open={pending !== null}
      onClose={() => setPending(null)}
      onConfirm={() => {
        pending?.proceed(pending.grants);
        setPending(null);
      }}
      title={
        hasWorkflows
          ? "Remove connector from workflows?"
          : "Remove connector from git sync?"
      }
      description={
        hasWorkflows ? (
          <>
            Removing this access means{" "}
            <Count n={workflowCount} noun="workflow" /> will need a connector
            replaced.
          </>
        ) : (
          `This removes ${appName} from git sync.`
        )
      }
      confirmLabel="Continue"
      destructive
    >
      {pending && (
        <DisconnectWarning {...pending.impact} chatCount={chatCount} />
      )}
    </ConfirmModal>
  );

  return { guardCommit, modal };
}
